/**
 * The GS1 format-notation interpreter — General Specifications 26.0 §7.11.
 *
 * The specification describes every AI's value as a compact notation:
 *
 *   N14      exactly 14 digits                       (GTIN)
 *   N18      exactly 18 digits                       (SSCC)
 *   X..20    1 to 20 characters from CSET 82         (batch/lot, serial)
 *   N..8     1 to 8 digits                           (variable numeric)
 *   Y..30    1 to 30 characters from CSET 39         (CPID)
 *   Z..25    1 to 25 characters from CSET 64         (compressed forms)
 *   N13,X..17  a fixed part followed by a variable part  (GDTI)
 *
 * Interpreting the notation rather than hand-writing a validator per AI is the
 * point: the AI table stays declarative data that can be diffed against the
 * printed specification, and there is exactly one place where "1 to 20" can be
 * got wrong. With ~20 path AIs and a growing attribute set, per-AI validators
 * would be ~20 opportunities for an off-by-one that only shows up on a printed
 * carton.
 */
import { isInCharset } from "./charset.js";
import { GS1Error } from "./errors.js";
// `N14`, `X..20`, `Z..25`, `X..16?`. The `..` is the specification's
// variable-length mark; the trailing `?` is ours, marking an optional
// component. GS1 prints optionality in prose beside the table rather than in
// the notation, so it has to be encoded somewhere — better here, where the
// parser can act on it, than as a special case in the AI table.
const COMPONENT = /^([NXYZ])(\.\.)?([0-9]{1,2})(\?)?$/;
/**
 * Parse a format string such as `"N13,X..17"` into components.
 *
 * @throws {Error} on a malformed notation. This is a programming error in our
 *   own AI table, never something a request can trigger, so it is a plain Error
 *   rather than a GS1Error — it must not be mapped to a 400.
 */
export function parseFormat(notation) {
    const components = notation.split(",").map((part) => {
        const m = COMPONENT.exec(part);
        if (m === null) {
            throw new Error(`Malformed GS1 format notation ${JSON.stringify(part)} in ${JSON.stringify(notation)}.`);
        }
        const charset = m[1];
        const variable = m[2] !== undefined;
        const optional = m[4] !== undefined;
        const max = Number(m[3]);
        return {
            charset,
            min: optional ? 0 : variable ? 1 : max,
            max,
            fixed: !variable,
            optional,
        };
    });
    // A variable-length component can only be split unambiguously if it is last.
    // The specification never defines a notation with a variable head, so this is
    // a typo in OUR AI table rather than anything a request can express.
    //
    // Checked here, at parse time, rather than in validateFormat, and the
    // distinction matters: the AI table is parsed once at module load, so a bad
    // notation now fails at import — in CI, on every developer's machine, before
    // anything is deployed. Checked at validation time it would import cleanly
    // and throw on every matching scan instead, which is a 500 in the hot path
    // caused by a typo. A resolver must not have that failure mode; spec §3.1's
    // whole point is that the request path degrades to staleness, never to an
    // error.
    for (let i = 0; i < components.length - 1; i++) {
        const c = components[i];
        if (!c.fixed) {
            throw new Error(`Malformed GS1 format notation ${JSON.stringify(notation)}: a variable-length ` +
                `component may only appear last, otherwise the value cannot be split ` +
                `unambiguously.`);
        }
        // Same argument for optionality: an absent component in the middle makes
        // every following component's offset ambiguous.
        if (c.optional) {
            throw new Error(`Malformed GS1 format notation ${JSON.stringify(notation)}: an optional ` +
                `component may only appear last, otherwise the value cannot be split ` +
                `unambiguously.`);
        }
    }
    let minLength = 0;
    let maxLength = 0;
    for (const c of components) {
        minLength += c.min;
        maxLength += c.max;
    }
    return { components, minLength, maxLength };
}
/**
 * Validate a decoded AI value against a parsed format.
 *
 * Single-component formats are checked directly. Multi-component formats are
 * checked as a whole: a fixed head followed by a variable tail can be split
 * unambiguously, which covers every multi-component AI in the specification —
 * GDTI (`N13,X..17`), GCN (`N13,N..12`) and GRAI (`N14,X..16`) all have a fixed
 * head. The ambiguous case — a variable component that is not last — is
 * rejected by `parseFormat` at module load and cannot reach here.
 *
 * This function therefore throws **only** `GS1Error`. That is a deliberate
 * property, not an accident: every failure it can produce is caused by request
 * data and maps cleanly onto a 400, so the resolver never has to decide at
 * runtime whether a thrown value is a client problem or our own.
 *
 * @throws {GS1Error} `LENGTH` or `CHARSET`
 */
export function validateFormat(value, format, ai) {
    if (value.length < format.minLength || value.length > format.maxLength) {
        throw new GS1Error("LENGTH", `AI ${ai} takes ${describeLength(format)}; received ${value.length}.`, ai);
    }
    let offset = 0;
    for (let i = 0; i < format.components.length; i++) {
        const component = format.components[i];
        const isLast = i === format.components.length - 1;
        // Non-last components are always fixed-length — parseFormat guarantees it —
        // so `component.max` is an exact width here, not an upper bound.
        const slice = isLast
            ? value.slice(offset)
            : value.slice(offset, offset + component.max);
        // An absent optional component is not a charset failure. Only reachable on
        // the last component, since parseFormat rejects optionality anywhere else.
        if (component.optional && slice.length === 0)
            continue;
        if (!isInCharset(slice, component.charset)) {
            throw new GS1Error("CHARSET", `AI ${ai} component ${i + 1} must be ${charsetName(component.charset)}; ` +
                `received ${JSON.stringify(slice)}.`, ai);
        }
        offset += slice.length;
    }
}
function charsetName(charset) {
    if (charset === "N")
        return "numeric";
    if (charset === "X")
        return "CSET 82";
    if (charset === "Y")
        return "CSET 39";
    return "CSET 64";
}
function describeLength(format) {
    return format.minLength === format.maxLength
        ? `exactly ${format.maxLength} characters`
        : `${format.minLength} to ${format.maxLength} characters`;
}
//# sourceMappingURL=format.js.map